import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { LecturesDataTable } from "@/components/lectures/lectures.data-table";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { lecturesApi } from "@/services/lectures/lectures.api";
import { $fetch } from "@/lib/fetch";
import {
  type LectureDto,
  type LectureFormDto,
  lecturesDtoSchema,
} from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { type LectureEntity } from "slde-api/db-schemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { LectureForm } from "@/components/lectures/lecture.form";

export default function LecturesPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [lectureToUpdate, setLectureToUpdate] = useState<LectureDto | null>(
    null,
  );

  const {
    data: { items },
  } = useSuspenseQuery({
    queryKey: lecturesApi.lectures.queryKey(),
    queryFn: async () =>
      await $fetch(lecturesApi.lectures.route(), {
        output: lecturesDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createLecture, isPending: isCreatingLecture } = useMutation({
    mutationFn: async (form: LectureFormDto) =>
      await $fetch(lecturesApi.lectures.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Wykład utworzono pomyślnie");
      setCreateDialogOpen(false);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu wykładu: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateLecture, isPending: isUpdatingLecture } = useMutation({
    mutationFn: async ({ id, ...form }: LectureEntity) =>
      await $fetch(lecturesApi.lecture.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano wyklad");
      setLectureToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji wykładu: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteLecture, isPending: isDeletingLecture } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(lecturesApi.lecture.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Wykład został usunięty");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu wykładu: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie wykładami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj wykład
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <LecturesDataTable
          data={items}
          actions={{
            onUpdateClick: (lecture) => setLectureToUpdate(lecture),
            onDeleteClick: (id) => deleteLecture({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!lectureToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setLectureToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!lectureToUpdate ? "Edytuj wykład" : "Dodaj wykład"}
            </DialogTitle>
          </DialogHeader>

          <LectureForm
            onSubmit={(form) =>
              !!lectureToUpdate
                ? updateLecture({ id: lectureToUpdate.id, ...form })
                : createLecture(form)
            }
            defaultValues={lectureToUpdate}
            disabled={
              isCreatingLecture || isUpdatingLecture || isDeletingLecture
            }
            loading={
              isCreatingLecture || isUpdatingLecture || isDeletingLecture
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
